import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Card, CardContent, CardTitle } from "@/shared/components/ui/card";
import { useTenant } from "@/domains/tenant/hooks";
import { createReportTemplateQueries } from "./queries";
import type {
  SourceTemplatePreset,
  TemplateStepPreset,
} from "@/domains/templates/mutations";
import { parseInsufficientCreditsError } from "@/domains/billing/errors";
import { getErrorMessage } from "@/shared/lib/error-message";
import { tenantQueries } from "@/domains/tenant/queries";
import { ContactSalesButton } from "@/domains/billing/components";
import { useCreateReport } from "../../hooks/use-create-report";
import type {
  InputFieldDefinition,
  UiTemplateSection,
  VariableValueInput,
  ScopedVariableValueInput,
} from "@/domains/templates/types";
import { ChevronDown, ChevronRight, Link2, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  RuntimeVariableInput,
  bindScopedChildDefinitions,
  buildRuntimeVariableValues,
  clearStepRuntimeValue,
  fieldValueKey,
  requireScopedVariableValues,
  resolveStepRuntimeValue,
  resolveRuntimeDefaultValue,
  resolveStepRuntimeDefaultValue,
  setStepRuntimeValue,
  shouldShowChildVariables,
} from "./runtime-variable-inputs";
import { resolveCreateReportFieldNames } from "./resolve-input-field-names";

type CreateReportDialogProps = {
  trigger?: ReactNode;
  sourceTemplate?: SourceTemplatePreset;
  initialOpen?: boolean;
};

const EMPTY_CHILD_FIELD_MAP = new Map<string, InputFieldDefinition[]>();

function fieldLabel(field: InputFieldDefinition) {
  return field.variableNameFrontend ?? field.description ?? field.key;
}

function isFieldRequired(field: InputFieldDefinition) {
  return field.required !== false;
}

function childRecordToMap(
  value: Record<string, InputFieldDefinition[]>,
): Map<string, InputFieldDefinition[]> {
  return new Map(Object.entries(value));
}

function requiredRuntimeValuesProvided(
  fields: InputFieldDefinition[],
  childFieldsByParentKey: Map<string, InputFieldDefinition[]>,
  getValue: (field: InputFieldDefinition) => string,
) {
  return fields.every((field) => {
    const value = getValue(field);
    if (isFieldRequired(field) && value.trim().length === 0) return false;
    if (!shouldShowChildVariables(field, value)) return true;
    return (childFieldsByParentKey.get(field.key) ?? []).every(
      (child) => !isFieldRequired(child) || getValue(child).trim().length > 0,
    );
  });
}

export function CreateReportDialog({
  trigger,
  sourceTemplate,
  initialOpen = false,
}: CreateReportDialogProps) {
  const isFromTemplate = Boolean(sourceTemplate);
  const templateName = sourceTemplate?.name?.trim() || "Current template";
  const [open, setOpen] = useState(initialOpen);
  const [name, setName] = useState(() =>
    initialOpen && isFromTemplate ? `${templateName} Report` : "",
  );
  const [description, setDescription] = useState("");
  const [selectedTemplateIdOverride, setSelectedTemplateIdOverride] =
    useState("");
  const [context, setContext] = useState(() =>
    initialOpen && isFromTemplate ? (sourceTemplate?.context ?? "") : "",
  );
  const [templateInputValues, setTemplateInputValues] = useState<
    Record<string, string>
  >({});
  const [stepInputValues, setStepInputValues] = useState<
    Record<string, Record<string, string>>
  >({});
  const [templateChildFields, setTemplateChildFields] = useState<
    Record<string, InputFieldDefinition[]>
  >({});
  const [stepChildFields, setStepChildFields] = useState<
    Record<string, Record<string, InputFieldDefinition[]>>
  >({});
  const [isTemplateFieldsOpen, setIsTemplateFieldsOpen] = useState(true);
  const [isSectionFieldsOpen, setIsSectionFieldsOpen] = useState(false);

  const navigate = useNavigate();
  const { data: tenantData } = useTenant();
  const isPublicTenant = tenantData?.tenant?.tenant_name === "Public";
  const tenantId = tenantData?.tenant?.tenant_id ?? "";

  const { data: templates, isLoading: templatesLoading } = useQuery({
    ...createReportTemplateQueries.list(
      { search: "", showGlobal: true },
      tenantId,
    ),
    enabled: !isFromTemplate && Boolean(tenantId),
  });

  const orderedTemplates = useMemo(
    () => [
      ...(templates ?? []).filter((template) => !template.isGlobal),
      ...(templates ?? []).filter((template) => template.isGlobal),
    ],
    [templates],
  );
  const collections = tenantData?.collections ?? [];
  const standardCollectionId =
    collections.find(
      (collection) =>
        collection.tenant_collection_name.toLowerCase() === "standard",
    )?.tenant_collection_id ??
    collections[0]?.tenant_collection_id ??
    "";
  const collectionId = standardCollectionId;
  const selectedTemplateId =
    sourceTemplate?.id ??
    (selectedTemplateIdOverride ||
      (open ? (orderedTemplates[0]?.id ?? "") : ""));
  const sourceTemplateDefaultsKey = open
    ? [
        sourceTemplate?.id ?? "",
        templateName,
        sourceTemplate?.context ?? "",
      ].join(":")
    : "";
  const runtimeValuesResetKey = open
    ? `${selectedTemplateId}:${sourceTemplate?.id ?? ""}`
    : "";
  const [
    appliedSourceTemplateDefaultsKey,
    setAppliedSourceTemplateDefaultsKey,
  ] = useState(sourceTemplateDefaultsKey);
  const [appliedRuntimeValuesResetKey, setAppliedRuntimeValuesResetKey] =
    useState(runtimeValuesResetKey);

  if (appliedSourceTemplateDefaultsKey !== sourceTemplateDefaultsKey) {
    setAppliedSourceTemplateDefaultsKey(sourceTemplateDefaultsKey);
    if (open && isFromTemplate) {
      setName(`${templateName} Report`);
      setDescription("");
      setContext(sourceTemplate?.context ?? "");
    }
  }

  if (appliedRuntimeValuesResetKey !== runtimeValuesResetKey) {
    setAppliedRuntimeValuesResetKey(runtimeValuesResetKey);
    if (open) {
      setTemplateInputValues({});
      setStepInputValues({});
      setTemplateChildFields({});
      setStepChildFields({});
    }
  }

  const {
    data: selectedTemplateDetail,
    isLoading: selectedTemplateDetailLoading,
  } = useQuery({
    ...createReportTemplateQueries.detail(selectedTemplateId),
    enabled: !isFromTemplate && Boolean(selectedTemplateId),
  });

  const {
    data: selectedTemplateSections,
    isLoading: selectedTemplateSectionsLoading,
  } = useQuery({
    ...createReportTemplateQueries.steps(selectedTemplateId),
    enabled: !isFromTemplate && Boolean(selectedTemplateId),
  });
  const activeTemplateId = sourceTemplate?.id ?? selectedTemplateId;
  const { data: linkedAnalyses, isLoading: linkedAnalysesLoading } = useQuery({
    ...createReportTemplateQueries.linkedAnalyses(),
    enabled: open,
  });
  const { data: linkedAnalysisSteps, isLoading: linkedAnalysisStepsLoading } =
    useQuery({
      ...createReportTemplateQueries.linkedAnalysisSteps(
        activeTemplateId ?? "",
      ),
      enabled: open && Boolean(activeTemplateId),
    });
  const { data: tenantVariableConfig } = useQuery({
    ...tenantQueries.variables(tenantId),
    enabled: open && Boolean(tenantId),
  });

  const selectedTemplateSteps: TemplateStepPreset[] = useMemo(
    () =>
      (selectedTemplateSections ?? []).map((step: UiTemplateSection) => ({
        id: step.id,
        name: step.name,
        order: step.order,
        inputFields: step.inputFields,
        scopedInputFields: step.scopedInputFields,
      })),
    [selectedTemplateSections],
  );

  const rawTemplateInputFields: InputFieldDefinition[] = useMemo(
    () =>
      isFromTemplate
        ? (sourceTemplate?.inputFields ?? [])
        : (selectedTemplateDetail?.inputFields ?? []),
    [isFromTemplate, sourceTemplate?.inputFields, selectedTemplateDetail],
  );

  const rawTemplateScopedInputFields: InputFieldDefinition[] = useMemo(
    () =>
      isFromTemplate
        ? (sourceTemplate?.scopedInputFields ??
          sourceTemplate?.inputFields ??
          [])
        : (selectedTemplateDetail?.scopedInputFields ?? []),
    [isFromTemplate, sourceTemplate, selectedTemplateDetail],
  );

  const rawTemplateSteps: TemplateStepPreset[] = useMemo(
    () =>
      isFromTemplate ? (sourceTemplate?.steps ?? []) : selectedTemplateSteps,
    [isFromTemplate, sourceTemplate?.steps, selectedTemplateSteps],
  );

  const resolvedTemplateFields = useMemo(
    () =>
      resolveCreateReportFieldNames({
        inputFields: rawTemplateInputFields,
        scopedInputFields: rawTemplateScopedInputFields,
        steps: rawTemplateSteps,
        catalogVariables: tenantVariableConfig?.variables ?? [],
      }),
    [
      rawTemplateInputFields,
      rawTemplateScopedInputFields,
      rawTemplateSteps,
      tenantVariableConfig?.variables,
    ],
  );
  const activeTemplateInputFields = resolvedTemplateFields.inputFields;
  const activeTemplateScopedInputFields =
    resolvedTemplateFields.scopedInputFields;
  const activeTemplateSteps = resolvedTemplateFields.steps;

  const tenantValuesByVariableId = useMemo(
    () =>
      new Map(
        (tenantVariableConfig?.variable_values ?? []).map((value) => [
          value.variable_id,
          value.variable_value,
        ]),
      ),
    [tenantVariableConfig?.variable_values],
  );

  function resetForm() {
    if (isFromTemplate) {
      setName(`${templateName} Report`);
      setDescription("");
      setContext(sourceTemplate?.context ?? "");
      setTemplateInputValues({});
      setStepInputValues({});
      setTemplateChildFields({});
      setStepChildFields({});
      return;
    }

    setName("");
    setDescription("");
    setSelectedTemplateIdOverride("");
    setContext("");
    setTemplateInputValues({});
    setStepInputValues({});
    setTemplateChildFields({});
    setStepChildFields({});
  }

  const getDefaultInputValue = (field: InputFieldDefinition) => {
    const tenantValue =
      field.sourceVariableId &&
      tenantValuesByVariableId.has(field.sourceVariableId)
        ? tenantValuesByVariableId.get(field.sourceVariableId)
        : undefined;
    return resolveRuntimeDefaultValue(field, tenantValue);
  };

  const getTemplateInputValue = (field: InputFieldDefinition) =>
    templateInputValues[field.key] ?? getDefaultInputValue(field);

  const getTemplateRuntimeValue = (field: InputFieldDefinition) =>
    templateInputValues[field.key] ??
    templateInputValues[fieldValueKey(field)] ??
    getDefaultInputValue(field);

  const getStepSourceTemplateField = (field: InputFieldDefinition) =>
    activeTemplateInputFields.find(
      (templateField) =>
        Boolean(field.sourceVariableId) &&
        field.sourceVariableId === templateField.variableId,
    );

  const hasStepFieldTemplateLink = (field: InputFieldDefinition) =>
    Boolean(getStepSourceTemplateField(field));

  const hasStepFieldExplicitValue = (
    stepId: string,
    field: InputFieldDefinition,
  ) => {
    const stepValues = stepInputValues[stepId] ?? {};
    return (
      Object.prototype.hasOwnProperty.call(stepValues, fieldValueKey(field)) ||
      Object.prototype.hasOwnProperty.call(stepValues, field.key)
    );
  };

  const isStepFieldLinked = (stepId: string, field: InputFieldDefinition) =>
    hasStepFieldTemplateLink(field) &&
    !hasStepFieldExplicitValue(stepId, field);

  const getStepRuntimeValue = (stepId: string, field: InputFieldDefinition) => {
    const templateField = getStepSourceTemplateField(field);
    const tenantValue =
      field.sourceVariableId &&
      tenantValuesByVariableId.has(field.sourceVariableId)
        ? tenantValuesByVariableId.get(field.sourceVariableId)
        : undefined;
    return resolveStepRuntimeValue(
      stepInputValues[stepId],
      field,
      resolveStepRuntimeDefaultValue(
        field,
        templateField ? getTemplateRuntimeValue(templateField) : undefined,
        tenantValue,
      ),
    );
  };

  const relinkStepFieldToTemplate = (
    stepId: string,
    field: InputFieldDefinition,
  ) => {
    setStepInputValues((current) => {
      const stepValues = current[stepId];
      if (!stepValues) return current;

      const remainingStepValues = clearStepRuntimeValue(stepValues, field);
      if (Object.keys(remainingStepValues).length === 0) {
        const remainingSteps = { ...current };
        delete remainingSteps[stepId];
        return remainingSteps;
      }

      return {
        ...current,
        [stepId]: remainingStepValues,
      };
    });
  };

  const isTemplateFieldMissing = (key: string) =>
    activeTemplateInputFields.some(
      (field) =>
        field.key === key &&
        isFieldRequired(field) &&
        getTemplateInputValue(field).trim().length === 0,
    );
  const isStepFieldMissing = (stepId: string, field: InputFieldDefinition) =>
    isFieldRequired(field) &&
    getStepRuntimeValue(stepId, field).trim().length === 0;

  const buildDisplayInputResults = (
    fields: InputFieldDefinition[],
    getValue: (field: InputFieldDefinition) => string,
  ): Record<string, string> =>
    fields.reduce<Record<string, string>>((results, field) => {
      const label = field.variableNameFrontend?.trim() || field.key.trim();
      if (label) results[label] = getValue(field).trim();
      return results;
    }, {});

  const buildVariableValues = (
    fields: InputFieldDefinition[],
    getValue: (field: InputFieldDefinition) => string,
    childFieldsByParentKey: Map<string, InputFieldDefinition[]> = new Map(),
  ): VariableValueInput[] =>
    buildRuntimeVariableValues(fields, childFieldsByParentKey, getValue);

  const templateChildFieldMap = useMemo(
    () => childRecordToMap(templateChildFields),
    [templateChildFields],
  );

  const stepChildFieldMaps = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(stepChildFields).map(([stepId, childFields]) => [
          stepId,
          childRecordToMap(childFields),
        ]),
      ),
    [stepChildFields],
  );

  const allTemplateInputValuesProvided = activeTemplateInputFields.every(
    (field) => {
      const value = getTemplateRuntimeValue(field);
      if (isFieldRequired(field) && value.trim().length === 0) return false;
      if (!shouldShowChildVariables(field, value)) return true;
      return requiredRuntimeValuesProvided(
        templateChildFieldMap.get(field.key) ?? [],
        EMPTY_CHILD_FIELD_MAP,
        getTemplateRuntimeValue,
      );
    },
  );

  const allStepInputValuesProvided = activeTemplateSteps.every((step) =>
    requiredRuntimeValuesProvided(
      step.inputFields,
      stepChildFieldMaps[step.id] ?? EMPTY_CHILD_FIELD_MAP,
      (field) => getStepRuntimeValue(step.id, field),
    ),
  );

  const {
    mutate: createReport,
    isPending,
    error: createReportError,
    reset: resetCreateReport,
  } = useCreateReport();
  const creditError = parseInsufficientCreditsError(createReportError);
  const createError =
    createReportError && !creditError
      ? getErrorMessage(
          createReportError,
          "There was an error adding the report. Please try again.",
        )
      : null;

  function clearCreateReportError() {
    if (createReportError) resetCreateReport();
  }

  function handleCreateReport() {
    if (!tenantData) return;

    const templateId = sourceTemplate?.id ?? selectedTemplateId;
    if (!templateId) return;

    const displayInputResults = buildDisplayInputResults(
      activeTemplateInputFields,
      getTemplateRuntimeValue,
    );
    const stepDisplayInputResults = activeTemplateSteps.reduce<
      Record<string, Record<string, string>>
    >((acc, step) => {
      acc[step.id] = buildDisplayInputResults(step.inputFields, (field) =>
        getStepRuntimeValue(step.id, field),
      );
      return acc;
    }, {});
    const variableValues = requireScopedVariableValues(
      buildVariableValues(
        activeTemplateInputFields,
        getTemplateRuntimeValue,
        templateChildFieldMap,
      ),
    );
    const stepVariableValues = activeTemplateSteps.reduce<
      Record<string, ScopedVariableValueInput[]>
    >((acc, step) => {
      acc[step.id] = requireScopedVariableValues(
        buildVariableValues(
          step.inputFields,
          (field) => getStepRuntimeValue(step.id, field),
          stepChildFieldMaps[step.id] ?? EMPTY_CHILD_FIELD_MAP,
        ),
      );
      return acc;
    }, {});

    resetCreateReport();
    createReport(
      {
        templateId,
        name,
        description,
        context,
        collectionId,
        displayInputResults,
        stepDisplayInputResults,
        variableValues,
        stepVariableValues,
      },
      {
        onSuccess: (analysisId) => {
          resetForm();
          setOpen(false);
          void navigate(`/reports/${analysisId}`);
        },
      },
    );
  }

  const stepCount = activeTemplateSteps.length;
  const hasTemplateSteps = stepCount > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        clearCreateReportError();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <div
          className={
            isPublicTenant
              ? "pointer-events-none select-none blur-[1.5px] opacity-60"
              : undefined
          }
        >
          <DialogHeader className="mb-4">
            <DialogTitle>Create report from template</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="analysis-name">Name</Label>
              <Input
                id="analysis-name"
                value={name}
                onChange={(event) => {
                  clearCreateReportError();
                  setName(event.target.value);
                }}
                placeholder="e.g. Q4 brand report"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="analysis-description">Description</Label>
              <Input
                id="analysis-description"
                value={description}
                onChange={(event) => {
                  clearCreateReportError();
                  setDescription(event.target.value);
                }}
                placeholder="Short description of this report"
              />
            </div>

            {isFromTemplate ? (
              <div className="grid gap-2">
                <Label>Template</Label>
                <Card>
                  <CardContent className="flex flex-row items-center justify-between gap-3 py-3">
                    <CardTitle className="text-base font-medium">
                      {templateName}
                    </CardTitle>
                    <span className="text-sm text-muted-foreground">
                      {stepCount} {stepCount === 1 ? "section" : "sections"}
                    </span>
                  </CardContent>
                </Card>
                {!hasTemplateSteps && (
                  <p className="text-sm text-muted-foreground">
                    This template has no sections yet.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid gap-2">
                <Label htmlFor="template">Template</Label>
                <select
                  id="template"
                  className="bg-background border-input focus:ring-ring/50 focus-visible:ring-ring/50 rounded-md border p-2 text-sm capitalize outline-none focus:ring-2"
                  value={selectedTemplateId}
                  onChange={(event) => {
                    clearCreateReportError();
                    setSelectedTemplateIdOverride(event.target.value);
                  }}
                  disabled={templatesLoading}
                >
                  <option value="" disabled>
                    {templatesLoading
                      ? "Loading templates..."
                      : "Select a template"}
                  </option>
                  {orderedTemplates.map((template) => (
                    <option
                      key={template.id}
                      value={template.id}
                      className="capitalize"
                    >
                      {template.name}
                    </option>
                  ))}
                </select>
                {!selectedTemplateSectionsLoading &&
                  selectedTemplateId &&
                  !hasTemplateSteps && (
                    <p className="text-sm text-muted-foreground">
                      This template has no sections yet.
                    </p>
                  )}
              </div>
            )}

            <Collapsible
              open={isTemplateFieldsOpen}
              onOpenChange={setIsTemplateFieldsOpen}
              className="grid gap-2"
            >
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto justify-start gap-1 p-0 text-left text-base font-semibold"
                >
                  {isTemplateFieldsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  Report Variables
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Card>
                  <CardContent className="grid gap-2 px-2 py-0">
                    <div className="grid gap-2">
                      <Label htmlFor="context">Additional Context</Label>
                      <Textarea
                        id="context"
                        value={context}
                        onChange={(event) => {
                          clearCreateReportError();
                          setContext(event.target.value);
                        }}
                        placeholder="Enter additional context for this report..."
                        rows={3}
                      />
                    </div>
                    {activeTemplateInputFields.length > 0 ? (
                      <div className="grid gap-2">
                        {activeTemplateInputFields.map((field) => {
                          const isMissing = isTemplateFieldMissing(field.key);
                          return (
                            <div
                              key={`analysis-input-${field.key}`}
                              className="grid gap-1"
                            >
                              <div
                                className={cn(
                                  "text-sm leading-5",
                                  isMissing ? "text-destructive" : undefined,
                                )}
                              >
                                <span className="font-medium">
                                  {fieldLabel(field)}
                                </span>
                                {field.description && (
                                  <span className="text-muted-foreground">
                                    {" - "}
                                    {field.description}
                                  </span>
                                )}
                              </div>
                              <RuntimeVariableInput
                                id={`analysis-input-${field.key}`}
                                field={field}
                                value={getTemplateRuntimeValue(field)}
                                analysisId={activeTemplateId}
                                isMissing={isMissing}
                                childValues={templateInputValues}
                                linkedAnalyses={linkedAnalyses}
                                linkedAnalysisSteps={linkedAnalysisSteps}
                                linkedAnalysesLoading={linkedAnalysesLoading}
                                linkedAnalysisStepsLoading={
                                  linkedAnalysisStepsLoading
                                }
                                onChange={(value) => {
                                  clearCreateReportError();
                                  setTemplateInputValues((current) => ({
                                    ...current,
                                    [field.key]: value,
                                  }));
                                }}
                                onChildChange={(childField, value) => {
                                  clearCreateReportError();
                                  setTemplateInputValues((current) => ({
                                    ...current,
                                    [childField.key]: value,
                                  }));
                                }}
                                onChildrenChange={(parentField, children) => {
                                  setTemplateChildFields((current) => ({
                                    ...current,
                                    [parentField.key]:
                                      bindScopedChildDefinitions(
                                        children,
                                        activeTemplateScopedInputFields,
                                      ),
                                  }));
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No report variables configured.
                      </p>
                    )}
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {activeTemplateSteps.some(
              (step) => step.inputFields.length > 0,
            ) && (
              <Collapsible
                open={isSectionFieldsOpen}
                onOpenChange={setIsSectionFieldsOpen}
                className="grid gap-2"
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto justify-start gap-1 p-0 text-left text-base font-semibold"
                  >
                    {isSectionFieldsOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    Report Section Variables
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="grid gap-2">
                  {activeTemplateSteps.map((step) => {
                    if (step.inputFields.length === 0) return null;
                    return (
                      <Card key={`step-input-${step.id}`}>
                        <CardContent className="grid gap-2 px-2 py-0">
                          <CardTitle className="text-base font-medium">
                            {step.name}
                          </CardTitle>
                          {step.inputFields.map((field) => {
                            const isLinked = isStepFieldLinked(step.id, field);
                            const canLinkToTemplate =
                              hasStepFieldTemplateLink(field);
                            const isMissing = isStepFieldMissing(
                              step.id,
                              field,
                            );

                            return (
                              <div
                                key={`step-input-${step.id}-${field.key}`}
                                className="grid gap-1"
                              >
                                <div
                                  className={cn(
                                    "text-sm leading-5",
                                    isMissing ? "text-destructive" : undefined,
                                  )}
                                >
                                  <span className="font-medium">
                                    {fieldLabel(field)}
                                  </span>
                                  {field.description && (
                                    <span className="text-muted-foreground">
                                      {" - "}
                                      {field.description}
                                    </span>
                                  )}
                                </div>
                                <div className="relative">
                                  <RuntimeVariableInput
                                    id={`step-input-${step.id}-${field.key}`}
                                    field={field}
                                    value={getStepRuntimeValue(step.id, field)}
                                    analysisId={activeTemplateId}
                                    isMissing={isMissing}
                                    className={cn(
                                      "pr-9",
                                      isLinked
                                        ? "bg-muted text-muted-foreground"
                                        : undefined,
                                    )}
                                    childValues={stepInputValues[step.id] ?? {}}
                                    linkedAnalyses={linkedAnalyses}
                                    linkedAnalysisSteps={linkedAnalysisSteps}
                                    excludedLinkedAnalysisStepIds={[step.id]}
                                    linkedAnalysesLoading={
                                      linkedAnalysesLoading
                                    }
                                    linkedAnalysisStepsLoading={
                                      linkedAnalysisStepsLoading
                                    }
                                    onChange={(value) => {
                                      clearCreateReportError();
                                      setStepInputValues((current) =>
                                        setStepRuntimeValue(
                                          current,
                                          step.id,
                                          field,
                                          value,
                                        ),
                                      );
                                    }}
                                    onChildChange={(childField, value) => {
                                      clearCreateReportError();
                                      setStepInputValues((current) =>
                                        setStepRuntimeValue(
                                          current,
                                          step.id,
                                          childField,
                                          value,
                                        ),
                                      );
                                    }}
                                    onChildrenChange={(
                                      parentField,
                                      children,
                                    ) => {
                                      setStepChildFields((current) => ({
                                        ...current,
                                        [step.id]: {
                                          ...(current[step.id] ?? {}),
                                          [parentField.key]:
                                            bindScopedChildDefinitions(
                                              children,
                                              step.scopedInputFields ??
                                                step.inputFields,
                                            ),
                                        },
                                      }));
                                    }}
                                  />
                                  {canLinkToTemplate && isLinked && (
                                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground">
                                      <Link2 className="h-4 w-4" />
                                    </span>
                                  )}
                                  {canLinkToTemplate && !isLinked && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                                      onClick={() =>
                                        relinkStepFieldToTemplate(
                                          step.id,
                                          field,
                                        )
                                      }
                                      aria-label={`Relink ${field.key} to template value`}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>

          {createError && (
            <Alert variant="destructive" className="mt-2">
              <AlertTitle>Couldn't create report</AlertTitle>
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          {creditError && (
            <Alert variant="destructive" className="mt-2">
              <AlertTitle>Credit limit reached</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{creditError.message}</p>
                {creditError.reason ===
                  "PROJECTED_ANALYSIS_CREDIT_LIMIT_EXCEEDED" &&
                  creditError.creditsUsed !== undefined &&
                  creditError.creditsLimit !== undefined &&
                  creditError.averageAnalysisCredits !== undefined &&
                  creditError.projectedCreditsUsed !== undefined && (
                    <p>
                      Current usage is {creditError.creditsUsed} /{" "}
                      {creditError.creditsLimit} credits. We reserve the average
                      analysis cost of {creditError.averageAnalysisCredits}{" "}
                      credits before starting, so the projected usage would be{" "}
                      {creditError.projectedCreditsUsed} /{" "}
                      {creditError.creditsLimit} credits.
                    </p>
                  )}
                {creditError.projectedCreditsOverLimit !== undefined &&
                  creditError.projectedCreditsOverLimit > 0 && (
                    <p>
                      This is {creditError.projectedCreditsOverLimit} credits
                      over the current limit.
                    </p>
                  )}
                <Link
                  className="font-medium underline"
                  to={creditError.detailsPath}
                >
                  View credit details
                </Link>
                {creditError.contactUrl && (
                  <ContactSalesButton className="mt-2" />
                )}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                resetCreateReport();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateReport}
              disabled={
                !name.trim() ||
                !collectionId ||
                !hasTemplateSteps ||
                !allTemplateInputValuesProvided ||
                !allStepInputValuesProvided ||
                isPublicTenant ||
                isPending ||
                (!isFromTemplate &&
                  (templatesLoading ||
                    selectedTemplateDetailLoading ||
                    selectedTemplateSectionsLoading))
              }
            >
              {isPending ? "Creating..." : createError ? "Retry" : "Create"}
            </Button>
          </DialogFooter>
        </div>
        {isPublicTenant && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/40">
            <ContactSalesButton />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export type { SourceTemplatePreset };
